import React, {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CreatedTripbook, InactiveCode, PrintedTripbook} from '../../domain/entities/B2BPackDetail.ts'
import {buildInvitationCodeUpdatePayload, InvitationCodeRecord, InvitationCodeTraveler} from '../../domain/entities/InvitationCode.ts'
import {TripbookFeedback} from '../../domain/entities/TripbookFeedback.ts'
import {TripbookPackage} from '../../domain/entities/Agency.ts'
import {EditInvitationCodeChanges, EditInvitationCodeDialog, EditInvitationCodeMode} from '../dialogs/EditInvitationCodeDialog.tsx'
import {AssignInvitationCodeDialog, AvailableInvitationCode} from '../dialogs/AssignInvitationCodeDialog.tsx'
import {EditTripbookFeedbackDialog} from '../dialogs/EditTripbookFeedbackDialog.tsx'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {SharedAlbumTravelersDialog} from '../dialogs/SharedAlbumTravelersDialog.tsx'
import {useCancelInvitationCode, useUpdateInvitationCode} from '../../hooks/useInvitationCodes.ts'
import {useTripbookFeedback} from '../../hooks/useTripbookFeedback.ts'
import {userReadableDescription} from '../../utils/UserReadableDescription.ts'
import {availablePackUnits, pageOptionsFor, CodeTableItem, codeGroupKind, groupCodeRows, isFreeCodeRow, isReservedRow, isSharedAlbum, unifyCodeRows, UnifiedCodeRow} from './unifyCodeRows.ts'
import {GroupBadge, GroupPill, GroupToggle, MemberCode, TravelerListButton} from './CodeGroupParts.tsx'
import {useExpandedGroups} from '../../hooks/useExpandedGroups.ts'
import {CodeStatusPill} from './CodeStatusPill.tsx'
import {TripbookSidePanel} from './TripbookSidePanel.tsx'
import {
    AddPill, CopyButton, Dash, DeliveryPill, FeedbackPopover,
    PdfCell, Td, TravelerCell, TripCell, CodeTableSection, usePagedSlice,
} from './PackActivityTables.tsx'
import {formatNumber} from '../../utils/formatNumber.ts'
import {deliverableLink} from '../../utils/redemptionUrl.ts'
import IconBook from '../../assets/icons/book.svg?react'
import IconPlus from '../../assets/icons/plus.svg?react'
import IconClose from '../../assets/icons/close.svg?react'
import IconPending from '../../assets/icons/pending.svg?react'
import IconAssigned from '../../assets/icons/assigned.svg?react'
import IconPackage from '../../assets/icons/package.svg?react'
import IconComment from '../../assets/icons/comment.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'

// The design renders the deliverable as "gift.51trips.com/<código>" — the host
// and path muted, the code itself in ink. Split on the code so the emphasis
// lands on the part the agency actually reads out to the traveler.
const splitDeliverableUrl = (rawUrl: string): {prefix: string; code: string} => {
    try {
        const url = new URL(rawUrl)
        const queryCode = url.searchParams.get('code')
        if (queryCode) return {prefix: `${url.host}${url.pathname}?code=`, code: queryCode}
        const segments = url.pathname.split('/').filter(Boolean)
        const last = segments.pop()
        if (!last) return {prefix: '', code: url.host}
        return {prefix: `${url.host}/${segments.length ? `${segments.join('/')}/` : ''}`, code: last}
    } catch {
        return {prefix: '', code: rawUrl}
    }
}

// Fixed lanes from the design, so "Estado" keeps room for its pill + inline
// action whichever of the three assigned states a row is in.
const ASSIGNED_TABLE_MIN_WIDTH = 1434

// Pending first (the one bucket the agency still has to act on), then
// delivered, then with a TripBook created — the design's order inside
// "Códigos asignados".
const ASSIGNED_ORDER: Record<string, number> = {NOT_ASSIGNED: 0, DELIVERED: 1, TRIP_CREATED: 2}
const isAssignedRow = (row: UnifiedCodeRow) =>
    (row.lifecycle === 'NOT_ASSIGNED' && isReservedRow(row)) || row.lifecycle === 'DELIVERED' || row.lifecycle === 'TRIP_CREATED'

interface AgencySelfServiceCodeSectionsProps {
    agencyId: string
    printed: PrintedTripbook[]
    created: CreatedTripbook[]
    inactive: InactiveCode[]
    invitationCodesById: Map<string, InvitationCodeRecord>
    onCodeCreated: () => void
    // Ids of reusable agency-wide codes (unlimitedRedemption) — the invitation-link
    // banner shows those separately, so they're filtered out of these tables.
    excludeCodeIds?: Set<string>
    // Decides which redemption host the assigned code's link is built on.
    primaryMarket?: string
    // Units bought across every pack. The banner counts down from this instead
    // of counting free codes: an agency on a generic link has no pool of codes
    // to count, just a quota.
    totalUnits: number
    // False for pay-per-use agencies (no packs). They have nothing to count
    // down from, so they get no banner.
    hasPacks: boolean
    // Feedback is staff-write-only (dialogs.feedbackStaffOnly) — false for the
    // agency's own self-service login, true when an admin views this same
    // layout via PacksTripbooksSection's viewAsAgency (see there). An admin
    // must always be able to add/edit feedback, regardless of view.
    canEditFeedback?: boolean
    // "Cancelar código": an agency can only cancel a code still pending
    // delivery (no trip yet); an admin (viewAsAgency) can also cancel one
    // already delivered or with a TripBook created, up to (not including)
    // printed. Same true-for-admin-only rule as canEditFeedback.
    canCancelBeyondPending?: boolean
    // Packs (units + pagination) and the agency's own pages, for the assign
    // dialog's "Paginación del TripBook" options.
    packages?: TripbookPackage[]
    agencyPages?: number
}

// Agency-facing version of PackActivityTables — same underlying printed/created/
// inactive data, but laid out as the "(Vista agencia) Codigo unico" design: an
// available-codes banner, then "Códigos asignados" (pending delivery, delivered
// and TripBook created in one list) and "TripBooks impresos". The agency reasons
// about a code's lifecycle by whether it has a traveler and whether it's been
// handed over, not by the internal "sin asignar" grouping. This applies to every self-service
// agency regardless of code format (unique or generic) — a generic link's
// redemptions and prefilled codes go through the exact same lifecycle fields.
// Feedback is read-only here for the agency's own self-service login, but
// writable when canEditFeedback is set (51trips-staff-write-only, see
// dialogs.feedbackStaffOnly — an admin is staff). "Productor" never appears
// (TripbookSidePanel already omits it).
export const AgencySelfServiceCodeSections: React.FC<AgencySelfServiceCodeSectionsProps> = ({
    agencyId, printed, created, inactive, invitationCodesById, onCodeCreated, excludeCodeIds, primaryMarket,
    totalUnits, hasPacks, canEditFeedback, canCancelBeyondPending, packages, agencyPages = 60,
}) => {
    const {t} = useTranslation('dashboard')
    const visibleInactive = useMemo(
        () => excludeCodeIds?.size ? inactive.filter((row) => !excludeCodeIds.has(row.codeId)) : inactive,
        [inactive, excludeCodeIds]
    )
    const rows = useMemo(
        () => unifyCodeRows(printed, created, visibleInactive, invitationCodesById, excludeCodeIds),
        [printed, created, visibleInactive, invitationCodesById, excludeCodeIds]
    )

    const availableRows = useMemo(() => rows.filter(isFreeCodeRow), [rows])
    // Not availableRows.length: that counts unassigned code documents, which
    // only exist for agencies on unique codes. The banner counts the pack quota
    // left, so it works the same on a generic link.
    // excludeCodeIds doubles as the generic-code id set here: it's built from
    // the same unlimitedRedemption codes (see PacksTripbooksSection), and only
    // those count consumed units per trip instead of per code (see unifyCodeRows).
    const availableUnits = useMemo(
        () => availablePackUnits(totalUnits, rows, invitationCodesById, excludeCodeIds),
        [totalUnits, rows, invitationCodesById, excludeCodeIds]
    )
    // Stable sort, so each state keeps the order unifyCodeRows gave it.
    const assignedRows = useMemo(
        () => rows.filter(isAssignedRow).sort((a, b) => ASSIGNED_ORDER[a.lifecycle] - ASSIGNED_ORDER[b.lifecycle]),
        [rows]
    )
    const printedRows = useMemo(() => rows.filter((r) => r.lifecycle === 'PRINTED'), [rows])

    const recordOf = (row: UnifiedCodeRow) => (row.codeId ? invitationCodesById.get(row.codeId) : undefined)

    // Groups render as one collapsible item each (see groupCodeRows).
    const assignedItems = useMemo(() => groupCodeRows(assignedRows, invitationCodesById), [assignedRows, invitationCodesById])
    const assignedPaging = usePagedSlice(assignedItems)
    // Free codes never carry a traveler, so they're never part of a group
    // (see groupCodeRows) — a plain list is enough, one row per code.
    const availablePaging = usePagedSlice(availableRows)
    const expandedGroups = useExpandedGroups()
    const travelersDialog = useRef<React.ElementRef<typeof SharedAlbumTravelersDialog>>(null)
    const [travelersCodeId, setTravelersCodeId] = useState<string | null>(null)
    const travelersRecord = travelersCodeId ? invitationCodesById.get(travelersCodeId) ?? null : null
    const printedPaging = usePagedSlice(printedRows)

    const [copied, setCopied] = useState<string | null>(null)
    const copyTimeout = useRef<ReturnType<typeof setTimeout>>()
    const editDialog = useRef<React.ElementRef<typeof EditInvitationCodeDialog>>(null)
    const assignDialog = useRef<React.ElementRef<typeof AssignInvitationCodeDialog>>(null)
    // The whole free pool, in order: one TripBook consumes the first code, "un
    // TripBook diferente para cada viajero" the first N (see AssignInvitationCodeDialog).
    const pageOptions = useMemo(
        () => pageOptionsFor(packages ?? [], agencyPages, rows, invitationCodesById, excludeCodeIds),
        [packages, agencyPages, rows, invitationCodesById, excludeCodeIds]
    )
    const availableCodes = useMemo<AvailableInvitationCode[]>(() => availableRows.flatMap((row) => {
        const record = row.codeId ? invitationCodesById.get(row.codeId) : undefined
        return record ? [{record, url: row.deliverableUrl ?? null}] : []
    }), [availableRows, invitationCodesById])
    const [editingCodeId, setEditingCodeId] = useState<string | null>(null)
    const [editingMode, setEditingMode] = useState<EditInvitationCodeMode>('traveler')
    const [openRowKey, setOpenRowKey] = useState<string | null>(null)
    const openRow = openRowKey ? rows.find((r) => r.key === openRowKey) ?? null : null
    const [feedbackPopoverKey, setFeedbackPopoverKey] = useState<string | null>(null)
    const feedbackDialog = useRef<React.ElementRef<typeof EditTripbookFeedbackDialog>>(null)
    const [feedbackRow, setFeedbackRow] = useState<UnifiedCodeRow | null>(null)
    const cancelDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const [cancelTarget, setCancelTarget] = useState<UnifiedCodeRow | null>(null)

    const {updateInvitationCode, isUpdatingInvitationCode} = useUpdateInvitationCode(agencyId)
    const {setTripbookFeedback, isSettingTripbookFeedback, deleteTripbookFeedback, isDeletingTripbookFeedback} = useTripbookFeedback(agencyId)
    const {cancelInvitationCode, isCancelingInvitationCode} = useCancelInvitationCode(agencyId)

    const editingRecord = editingCodeId ? invitationCodesById.get(editingCodeId) ?? null : null

    const copyLink = (key: string, url: string) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(key)
            clearTimeout(copyTimeout.current)
            copyTimeout.current = setTimeout(() => setCopied(null), 1400)
        })
    }

    const openEdit = (codeId: string, mode: EditInvitationCodeMode) => {
        setEditingCodeId(codeId)
        setEditingMode(mode)
        editDialog.current?.open()
    }

    const handleStatusChange = (row: UnifiedCodeRow, isDelivered: boolean) => {
        if (!row.codeId) return
        const record = invitationCodesById.get(row.codeId)
        if (!record) return
        updateInvitationCode(buildInvitationCodeUpdatePayload(record, {isDelivered})).catch(userReadableDescription)
    }

    const handleSaveEdit = (changes: EditInvitationCodeChanges) => {
        if (!editingRecord) return
        updateInvitationCode(buildInvitationCodeUpdatePayload(editingRecord, changes))
            .then(() => editDialog.current?.close())
            .catch(console.error)
    }

    const openFeedbackEditor = (row: UnifiedCodeRow) => {
        setFeedbackPopoverKey(null)
        setFeedbackRow(row)
        feedbackDialog.current?.open()
    }

    const openTravelerList = (codeId: string) => {
        setTravelersCodeId(codeId)
        travelersDialog.current?.open()
    }

    const handleSaveTravelers = (travelers: InvitationCodeTraveler[]) => {
        if (!travelersRecord) return Promise.resolve()
        return updateInvitationCode(buildInvitationCodeUpdatePayload(travelersRecord, {travelers}))
    }

    const handleSaveFeedback = (feedback: TripbookFeedback) => {
        if (!feedbackRow?.tripId || !feedbackRow.tripbookId) return
        setTripbookFeedback({tripId: feedbackRow.tripId, tripbookId: feedbackRow.tripbookId, payload: feedback})
            .then(() => feedbackDialog.current?.close())
    }

    const handleDeleteFeedback = () => {
        if (!feedbackRow?.tripId || !feedbackRow.tripbookId) return
        deleteTripbookFeedback({tripId: feedbackRow.tripId, tripbookId: feedbackRow.tripbookId})
            .then(() => feedbackDialog.current?.close())
    }

    // Only unique codes ("Solo aplicable para códigos únicos" — a generic link
    // is one document shared by every redemption, cancelling it would kill the
    // whole link). Pending delivery is cancellable by anyone; delivered/created
    // only by an admin (canCancelBeyondPending) — never a printed one.
    const canCancelRow = (row: UnifiedCodeRow) => {
        if (!row.codeId || excludeCodeIds?.has(row.codeId)) return false
        if (row.lifecycle === 'NOT_ASSIGNED') return true
        if (row.lifecycle === 'DELIVERED') return !!canCancelBeyondPending
        return false
    }

    const askCancel = (row: UnifiedCodeRow) => {
        setCancelTarget(row)
        cancelDialog.current?.open()
    }

    // Same fire-and-forget pattern as AgencyUsersSection's confirmDelete:
    // ConfirmDialog closes itself as soon as onConfirm returns truthy, so the
    // request just runs in the background after that.
    const confirmCancel = () => {
        if (!cancelTarget?.codeId) return false
        cancelInvitationCode(cancelTarget.codeId).catch(userReadableDescription)
        setCancelTarget(null)
        return true
    }

    const cancelButton = (row: UnifiedCodeRow) => (
        <button type="button" title={t('packs.cancelCode')} disabled={isCancelingInvitationCode}
                onClick={(e) => {e.stopPropagation(); askCancel(row)}}
                className="w-6 h-6 flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-danger/10 hover:text-danger disabled:opacity-40 disabled:cursor-not-allowed">
            <IconTrash className="size-3"/>
        </button>
    )

    // Always consumes a free code from the disponibles pool when there is one —
    // only an empty pool mints a brand-new code, so a pack agency can't end up
    // with more codes than units just by using this button.
    const openAssignDialog = () => {
        assignDialog.current?.open()
    }

    // --- Cell fragments ---

    const codeCell = (row: UnifiedCodeRow) => (
        <Td clip><span className="font-medium text-neutralBlack-900 tracking-wide whitespace-nowrap">{row.code ?? <Dash/>}</span></Td>
    )

    // Printed codes flag their group kind with a round badge next to the code.
    const printedCodeCell = (row: UnifiedCodeRow) => {
        const kind = codeGroupKind(recordOf(row))
        return (
            <Td clip>
                <span className="inline-flex items-center gap-[9px] font-medium text-neutralBlack-900 tracking-wide whitespace-nowrap">
                    {row.code ?? <Dash/>}
                    {kind && <GroupBadge kind={kind}/>}
                </span>
            </Td>
        )
    }


    // Full link inline (not a "Ver enlace" label): the agency reads the code
    // out of this column, so the URL is shown muted with the code highlighted,
    // and the copy affordance flips to a "Copiado" confirmation.
    const deliverableCell = (row: UnifiedCodeRow) => {
        const url = deliverableLink(row.deliverableUrl, row.code, primaryMarket)
        if (!url) return <Td clip><Dash/></Td>
        const {prefix, code} = splitDeliverableUrl(url)
        const copyKey = `deliverable-${row.key}`
        return (
            <Td clip>
                <span className="flex items-baseline gap-2 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <a href={url} target="_blank" rel="noreferrer"
                       className="block min-w-0 truncate text-[13px] text-neutralBlack-700 no-underline hover:text-neutralBlack-900 hover:underline">
                        <span className="text-neutralBlack-500">{prefix}</span>
                        <span className="font-medium text-neutralBlack-900">{code}</span>
                    </a>
                    {copied === copyKey ? (
                        <span className="flex-none text-[11.5px] text-[#18AE60]">{t('packs.copied')}</span>
                    ) : (
                        <CopyButton active={false} onClick={() => copyLink(copyKey, url)}/>
                    )}
                </span>
            </Td>
        )
    }

    const travelerCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td clip>
            {row.traveler?.name ? (
                row.codeId ? (
                    <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'traveler')}}
                            className="group flex items-center gap-2 text-left disabled:cursor-not-allowed">
                        <TravelerCell traveler={row.traveler}/>
                    </button>
                ) : <TravelerCell traveler={row.traveler}/>
            ) : row.codeId ? (
                <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'traveler')}} className="disabled:cursor-not-allowed">
                    <AddPill label={t('packs.addTraveler')}/>
                </button>
            ) : <Dash/>}
        </Td>
    )

    const tripCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td clip>
            {row.trip ? (
                row.codeId ? (
                    <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'trip')}}
                            className="group flex items-center gap-2 text-left disabled:cursor-not-allowed">
                        <TripCell trip={row.trip}/>
                    </button>
                ) : <TripCell trip={row.trip}/>
            ) : row.codeId ? (
                <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'trip')}} className="disabled:cursor-not-allowed">
                    <AddPill label={t('addTrip')}/>
                </button>
            ) : <Dash/>}
        </Td>
    )

    const tripbookCell = (row: UnifiedCodeRow) => (
        <Td>
            {row.flipbookUrl ? (
                <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <a href={row.flipbookUrl} target="_blank" rel="noreferrer" className="text-[13px] whitespace-nowrap text-[#3E63DD] hover:underline">{t('packs.viewTripbook')}</a>
                    <CopyButton active={copied === `tripbook-${row.key}`} onClick={() => copyLink(`tripbook-${row.key}`, row.flipbookUrl!)}/>
                </span>
            ) : <Dash/>}
        </Td>
    )

    // Feedback is staff-write-only (dialogs.feedbackStaffOnly): the agency's own
    // self-service login only ever views it, but an admin browsing this same
    // layout (canEditFeedback, see viewAsAgency) can always add or edit it too.
    const feedbackCell = (row: UnifiedCodeRow, align: 'left' | 'right' = 'left') => (
        <Td align={align}>
            <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                <span className="relative inline-block">
                    {row.feedback ? (
                        <button type="button" title={t('packs.viewTravelerFeedback')}
                                onClick={() => setFeedbackPopoverKey((k) => (k === row.key ? null : row.key))}
                                className="w-7 h-7 flex items-center justify-center rounded-full text-neutralBlack-700 hover:bg-neutralBlack-100 hover:text-neutralBlack-900">
                            <IconComment className="w-[15px] h-[15px]"/>
                        </button>
                    ) : row.tripbookId && canEditFeedback ? (
                        <button type="button" title={t('packs.addClientFeedback')} onClick={() => openFeedbackEditor(row)}
                                className="w-6 h-6 flex items-center justify-center rounded-full border border-dashed border-neutralBlack-300 text-neutralBlack-400 hover:text-neutralBlack-900 hover:border-neutralBlack-500">
                            <IconPlus className="size-2.5"/>
                        </button>
                    ) : null}
                    {feedbackPopoverKey === row.key && row.feedback && (
                        <FeedbackPopover row={row} canEdit={!!canEditFeedback} onEdit={() => openFeedbackEditor(row)} onClose={() => setFeedbackPopoverKey(null)}/>
                    )}
                </span>
                {canCancelRow(row) && cancelButton(row)}
            </span>
        </Td>
    )

    const deliveryCell = (row: UnifiedCodeRow) => (
        <Td>
            {row.delivery ? (
                <span className="flex flex-col gap-1.5 items-start">
                    <DeliveryPill status={row.delivery.status}/>
                    {row.delivery.trackingUrl && row.delivery.status !== 'DELIVERED' && (
                        <a href={row.delivery.trackingUrl} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="inline-flex items-center gap-1.5 text-[12px] whitespace-nowrap text-[#3E63DD] hover:underline">
                            <IconPackage className="size-5 flex-none"/>
                            {t('packs.sidePanelFollowOrder')}
                        </a>
                    )}
                </span>
            ) : <Dash/>}
        </Td>
    )

    // Grey pending-icon pill + a plain checkbox action to mark it delivered —
    // distinct from the dot-pill language used by creado/impreso, since this is
    // the one status the agency can flip by hand here.
    const pendingStatusCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td>
            <span className="inline-flex items-center gap-[9px] whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-black/[.06] bg-[#F1F1F4] px-2.5 py-1 text-xs font-medium text-neutralBlack-900 whitespace-nowrap">
                    <IconPending className="w-[13px] h-[13px] flex-none"/>
                    {t('packs.pendingStatusLabel')}
                </span>
                <button type="button" title={t('packs.markAsDelivered')} disabled={!hasRecord || isUpdatingInvitationCode}
                        onClick={() => handleStatusChange(row, true)}
                        className="group inline-flex items-center gap-[7px] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                    <span className="w-4 h-4 flex-none rounded-[5px] border-[1.5px] border-[#C6C6CE] bg-white group-enabled:group-hover:border-neutralBlack-900"/>
                    <span className="text-[12.5px] text-neutralBlack-500 group-enabled:group-hover:text-neutralBlack-900">{t('packs.statusDelivered')}</span>
                </button>
            </span>
        </Td>
    )

    const deliveredStatusCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td>
            <span className="inline-flex items-center gap-[7px]" onClick={(e) => e.stopPropagation()}>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#04A3B3]/[.22] bg-[#D8F3F6] px-2.5 py-1 text-xs font-medium text-[#046069] whitespace-nowrap">
                    <span className="w-[7px] h-[7px] rounded-full flex-none bg-[#04A3B3]"/>
                    {t('packs.statusDelivered')}
                </span>
                <button type="button" title={t('packs.markAsNotDelivered')} disabled={!hasRecord || isUpdatingInvitationCode}
                        onClick={() => handleStatusChange(row, false)}
                        className="w-[22px] h-[22px] flex-none flex items-center justify-center rounded-full text-[#C6C6CE] hover:bg-neutralBlack-100 hover:text-neutralBlack-900 disabled:opacity-40 disabled:cursor-not-allowed">
                    <IconClose className="w-[9px] h-[9px]"/>
                </button>
            </span>
        </Td>
    )

    // A single code is one copy; group rows show their size in bold, per the design.
    const copiesCell = () => <Td><span className="text-[13.5px] text-neutralBlack-700">1</span></Td>
    const groupCopiesCell = (copies: number) => <Td><span className="text-[13.5px] font-semibold text-neutralBlack-900">{copies}</span></Td>

    const statusCell = (row: UnifiedCodeRow, hasRecord: boolean) => {
        if (row.lifecycle === 'DELIVERED') return deliveredStatusCell(row, hasRecord)
        if (row.lifecycle === 'TRIP_CREATED') return <Td><CodeStatusPill lifecycle="TRIP_CREATED"/></Td>
        return pendingStatusCell(row, hasRecord)
    }

    const actionsCell = (row: UnifiedCodeRow) => row.lifecycle === 'TRIP_CREATED'
        ? feedbackCell(row, 'right')
        : <Td align="right">{canCancelRow(row) && cancelButton(row)}</Td>

    const renderAssignedRow = (row: UnifiedCodeRow) => {
        const record = recordOf(row)
        const hasRecord = !!record
        const isShared = isSharedAlbum(record)
        return (
            <tr key={row.key} onClick={() => setOpenRowKey(row.key)}
                className="cursor-pointer border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50">
                {codeCell(row)}
                {isShared ? groupCopiesCell(record!.copies!) : copiesCell()}
                {isShared ? <Td><GroupPill kind="shared"/></Td> : statusCell(row, hasRecord)}
                <PdfCell code={row.code}/>
                {deliverableCell(row)}
                {isShared ? <Td clip><TravelerListButton record={record!} onOpen={() => openTravelerList(record!.id)}/></Td> : travelerCell(row, hasRecord)}
                {tripCell(row, hasRecord)}
                {actionsCell(row)}
            </tr>
        )
    }

    // A member of a "Grupo - Álbum individual": the trip lives on the group row,
    // so it's left out here, and so is the copies count.
    const renderMemberRow = (row: UnifiedCodeRow) => {
        const hasRecord = !!recordOf(row)
        return (
            <tr key={row.key} onClick={() => setOpenRowKey(row.key)}
                className="cursor-pointer border-b border-neutralBlack-100 last:border-0 bg-neutralBlack-50 hover:bg-[#F3F3F5]">
                <Td clip><MemberCode code={row.code}/></Td>
                <Td/>
                {statusCell(row, hasRecord)}
                <PdfCell code={row.code}/>
                {deliverableCell(row)}
                {travelerCell(row, hasRecord)}
                <Td/>
                {actionsCell(row)}
            </tr>
        )
    }

    const renderAssignedItem = (item: CodeTableItem) => {
        if (item.kind === 'row') return renderAssignedRow(item.row)
        const isOpen = expandedGroups.isExpanded(item.key)
        const trip = item.rows.find((r) => r.trip)?.trip ?? null
        return (
            <React.Fragment key={item.key}>
                <tr onClick={() => expandedGroups.toggle(item.key)} aria-expanded={isOpen}
                    className="cursor-pointer border-b border-neutralBlack-100 last:border-0 bg-white hover:bg-neutralBlack-50">
                    <Td clip><GroupToggle isOpen={isOpen} count={item.rows.length}/></Td>
                    {groupCopiesCell(item.rows.length)}
                    <Td><GroupPill kind="individual"/></Td>
                    <Td/>
                    <Td/>
                    <Td/>
                    <Td clip>{trip ? <TripCell trip={trip}/> : <Dash/>}</Td>
                    <Td/>
                </tr>
                {isOpen && item.rows.map(renderMemberRow)}
            </React.Fragment>
        )
    }

    // Nothing to assign yet (no traveler, no trip) — just the code, its PDF/link
    // and the same "Añadir viajero"/"Añadir viaje" pills used elsewhere, which
    // move the row into "Códigos asignados" as soon as one is set.
    const renderAvailableRow = (row: UnifiedCodeRow) => {
        const hasRecord = !!row.codeId && invitationCodesById.has(row.codeId)
        return (
            <tr key={row.key} onClick={() => setOpenRowKey(row.key)}
                className="cursor-pointer border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50">
                {codeCell(row)}
                <PdfCell code={row.code}/>
                {deliverableCell(row)}
                {travelerCell(row, hasRecord)}
                {tripCell(row, hasRecord)}
                <Td align="right">{canCancelRow(row) && cancelButton(row)}</Td>
            </tr>
        )
    }

    const renderPrintedRow = (row: UnifiedCodeRow) => (
        <tr key={row.key} onClick={() => setOpenRowKey(row.key)}
            className="cursor-pointer border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50">
            {printedCodeCell(row)}
            <Td><CodeStatusPill lifecycle="PRINTED"/></Td>
            {travelerCell(row, !!row.codeId && invitationCodesById.has(row.codeId))}
            {tripCell(row, !!row.codeId && invitationCodesById.has(row.codeId))}
            {tripbookCell(row)}
            {deliveryCell(row)}
            {feedbackCell(row)}
        </tr>
    )

    return (
        <>
            <div className="flex flex-col gap-3">
                {/* Shown to every agency with a pack, including at 0: running out is
                    exactly when the agency needs to see the number, and hiding the
                    banner then would read as "the feature disappeared". Pay-per-use
                    agencies have no quota to count down, so they get nothing.
                    Wraps on narrow screens via flex-wrap — same layout on mobile,
                    with the button dropping to its own line. */}
                {hasPacks && (
                    <section className="bg-white border border-[#18AE60]/[.22] rounded-2xl shadow-sm px-6 py-[22px] flex items-center gap-5 flex-wrap">
                        <span className="w-[46px] h-[46px] flex-none rounded-[14px] bg-[#D9F5E4] flex items-center justify-center text-[#0F7A45]">
                            <IconBook className="w-[22px] h-[22px]"/>
                        </span>
                        <span className="flex-1 min-w-[200px] flex flex-col gap-[3px]">
                            <span className="flex items-baseline gap-[9px] flex-wrap">
                                <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.availableBannerLabel')}</span>
                                <span className="text-[26px] font-bold tracking-tight text-[#0F7A45]">{formatNumber(availableUnits)}</span>
                            </span>
                            <span className="text-[13px] leading-[1.5] text-neutralBlack-500">
                                {availableUnits === 0 ? t('packs.availableBannerEmptyHelp') : t('packs.availableBannerHelp')}
                            </span>
                        </span>
                        <button type="button" onClick={openAssignDialog} disabled={availableUnits === 0}
                                className="inline-flex items-center gap-[9px] h-12 px-6 rounded-full border border-[#0F7A45]/[.24] bg-[#DFF3E7] text-[15px] font-semibold text-[#0D6B3C] shadow-[0_1px_2px_rgba(15,122,69,.10)] hover:bg-[#CFEBDA] hover:border-[#0F7A45] whitespace-nowrap disabled:bg-neutralBlack-100 disabled:border-neutralBlack-200 disabled:text-neutralBlack-400 disabled:shadow-none disabled:cursor-not-allowed disabled:hover:bg-neutralBlack-100 disabled:hover:border-neutralBlack-200">
                            <IconPlus className="w-[15px] h-[15px]"/>
                            {t('packs.assignCodeToTraveler')}
                        </button>
                    </section>
                )}

                {/* Codes generated but not yet handed to a traveler — counted in the
                    banner above and offered by "Asignar código a un viajero", but
                    until now never listed on their own (see isFreeCodeRow). Collapsed
                    by default, same rule as "TripBooks impresos": only "Códigos
                    asignados" opens by default, the one list the agency must act on. */}
                <CodeTableSection
                    dotColor="bg-neutralBlack-400"
                    title={t('packs.availableSectionTitle')}
                    count={availableRows.length}
                    help={t('packs.availableSectionHelp')}
                    emptyLabel={t('packs.availableSectionEmpty')}
                    columns={[
                        {label: t('packs.columnCode'), width: 170},
                        {label: t('packs.columnPdf'), width: 200},
                        {label: t('packs.columnDeliverableLink'), width: 220},
                        {label: t('packs.columnTraveler'), width: 220},
                        {label: t('packs.columnTrip'), width: 220},
                        {label: '', width: 64, align: 'right'},
                    ]}
                    defaultOpen={false}
                    minWidth={1094}
                    paging={availablePaging}
                    totalItems={availableRows.length}
                    renderRow={renderAvailableRow}
                />

                {/* One list for every code with a traveler that isn't printed yet
                    (pending delivery, delivered, TripBook created) — the state lives
                    in the "Estado" column instead of splitting into three tables. */}
                <CodeTableSection
                    icon={<IconAssigned className="w-[18px] h-[18px] flex-none text-neutralBlack-900"/>}
                    title={t('packs.assignedSectionTitle')}
                    count={assignedRows.length}
                    help={t('packs.assignedSectionHelp')}
                    emptyLabel={t('packs.assignedSectionEmpty')}
                    columns={[
                        {label: t('packs.columnCode'), width: 170},
                        {label: t('packs.columnCopies'), width: 80},
                        {label: t('packs.columnStatus'), width: 320},
                        {label: t('packs.columnPdf'), width: 200},
                        {label: t('packs.columnDeliverableLink'), width: 200},
                        {label: t('packs.columnTraveler'), width: 200},
                        {label: t('packs.columnTrip'), width: 200},
                        {label: '', width: 64, align: 'right'},
                    ]}
                    defaultOpen={true}
                    minWidth={ASSIGNED_TABLE_MIN_WIDTH}
                    paging={assignedPaging}
                    totalItems={assignedItems.length}
                    renderRow={renderAssignedItem}
                    // Same action and same label as the banner's CTA, so it only
                    // belongs here when there is no banner (pay-per-use agencies,
                    // which have no quota to show). Otherwise the agency reads
                    // "Asignar código a un viajero" twice on one screen.
                    headerRight={hasPacks ? undefined : (
                        <button
                            type="button"
                            onClick={openAssignDialog}
                            className="flex-none inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#0F7A45]/[.24] bg-[#DFF3E7] text-[13.5px] font-semibold text-[#0D6B3C] shadow-[0_1px_2px_rgba(15,122,69,.10)] hover:bg-[#CFEBDA] hover:border-[#0F7A45] whitespace-nowrap"
                        >
                            <IconPlus className="size-3"/>
                            {t('packs.prefillCode')}
                        </button>
                    )}

                />

                {/* Collapsed by default — the design only opens "Códigos
                    asignados", the list the agency still has to act on. */}
                <CodeTableSection
                    dotColor="bg-[#18AE60]"
                    title={t('packs.printedSectionTitle')}
                    count={printedRows.length}
                    help={t('packs.printedSectionHelp')}
                    emptyLabel={t('packs.printedSectionEmpty')}
                    columns={[t('packs.columnCode'), t('packs.columnStatus'), t('packs.columnTraveler'), t('packs.columnTrip'), t('packs.columnTripbook'), t('packs.columnDelivery'), t('packs.columnFeedback')]}
                    minWidth={1000}
                    defaultOpen={false}
                    paging={printedPaging}
                    totalItems={printedRows.length}
                    renderRow={renderPrintedRow}
                />
            </div>

            <AssignInvitationCodeDialog
                ref={assignDialog}
                agencyId={agencyId}
                availableCodes={availableCodes}
                pageOptions={pageOptions}
                defaultPages={agencyPages}
                primaryMarket={primaryMarket}
                onAssigned={onCodeCreated}
            />
            <EditInvitationCodeDialog ref={editDialog} mode={editingMode} record={editingRecord} isSaving={isUpdatingInvitationCode} onSave={handleSaveEdit}/>
            <EditTripbookFeedbackDialog
                ref={feedbackDialog}
                code={feedbackRow?.code ?? null}
                feedback={feedbackRow?.feedback ?? null}
                isSaving={isSettingTripbookFeedback}
                isDeleting={isDeletingTripbookFeedback}
                onSave={handleSaveFeedback}
                onDelete={handleDeleteFeedback}
            />
            <SharedAlbumTravelersDialog
                ref={travelersDialog}
                record={travelersRecord}
                isSaving={isUpdatingInvitationCode}
                onSave={handleSaveTravelers}
            />
            <ConfirmDialog
                ref={cancelDialog}
                onConfirm={confirmCancel}
                title={t('packs.cancelCodeConfirmTitle')}
                message={t('packs.cancelCodeConfirmMessage')}
                confirmLabel={t('packs.cancelCode')}
                cancelLabel={t('cancel')}
            />

            {openRow && (
                <TripbookSidePanel
                    row={openRow}
                    packContext=""
                    canEditCode={!!openRow.codeId && invitationCodesById.has(openRow.codeId)}
                    canEditFeedback={!!canEditFeedback}
                    isDelivered={openRow.lifecycle === 'DELIVERED'}
                    itineraryUrl={openRow.codeId ? invitationCodesById.get(openRow.codeId)?.itinerary?.tripItineraryPdfUrl : undefined}
                    onClose={() => setOpenRowKey(null)}
                    onEditTraveler={() => openRow.codeId && openEdit(openRow.codeId, 'traveler')}
                    onEditTrip={() => openRow.codeId && openEdit(openRow.codeId, 'trip')}
                    onEditFeedback={() => openFeedbackEditor(openRow)}
                    onStatusChange={
                        openRow.lifecycle === 'NOT_ASSIGNED' || openRow.lifecycle === 'DELIVERED'
                            ? (delivered) => handleStatusChange(openRow, delivered)
                            : undefined
                    }
                />
            )}
        </>
    )
}
